import type { EditorView } from "prosemirror-view";
import type { Media, Tag } from "sdk";
import { createContext, type ParentProps, useContext } from "solid-js";
import { createStore } from "solid-js/store";
import { useRouter } from "./router";

export type ChannelTypeOption =
	| "Text"
	| "Voice"
	| "Category"
	| "Forum"
	| "Calendar"
	| "Document"
	| "Wiki";

export type Modal =
	| {
			type: "room_settings";
			room_id: string;
			page?: string;
	  }
	| {
			type: "channel_settings";
			channel_id: string;
			page?: string;
	  }
	| {
			type: "user_settings";
			page?: string;
	  }
	| {
			type: "alert";
			text: string;
	  }
	| {
			type: "confirm";
			text: string;
			cont: (confirmed: boolean) => void;
	  }
	| {
			type: "prompt";
			text: string;
			cont: (text: string | null) => void;
	  }
	| {
			type: "media";
			media: Media;
	  }
	| {
			type: "message_edits";
			channel_id: string;
			message_id: string;
	  }
	| {
			type: "reset_password";
	  }
	| {
			type: "logout";
	  }
	| {
			type: "palette";
	  }
	| {
			type: "channel_create";
			room_id: string;
	  }
	| {
			type: "tag_editor";
			forumChannelId: string;
			tag?: Tag;
			onSave?: (tag: Tag) => void;
			onClose?: () => void;
	  }
	| {
			type: "export_data";
	  }
	| {
			type: "view_reactions";
			channel_id: string;
			message_id: string;
	  }
	| {
			type: "privacy";
			room_id: string;
	  }
	| {
			type: "notifications";
			room_id: string;
	  }
	| {
			type: "invite_create";
			room_id?: string;
			channel_id?: string;
	  }
	| {
			type: "attachment";
			channel_id: string;
			local_id: string;
	  }
	| {
			type: "channel_topic";
			channel_id: string;
	  }
	| {
			type: "link";
			editor: EditorView;
	  }
	| {
			type: "kick";
			room_id: string;
			user_id: string;
	  }
	| {
			type: "ban";
			room_id: string;
			user_id?: string;
	  }
	| {
			type: "timeout";
			room_id: string;
			user_id: string;
	  }
	| {
			type: "camera_preview";
			stream: MediaStream;
	  }
	| {
			type: "room_create_or_join";
	  }
	| {
			type: "emoji_upload";
			room_id: string;
			media: Media;
	  }
	| {
			type: "thread_create";
			room_id: string;
			channel_id: string;
	  }
	| {
			type: "view_channel_nickname";
			initial: string;
			placeholder: string;
			cont: (nickname: string) => void;
	  };

export type ModalsController = {
	close: () => void;
	open: (modal: Modal) => void;
	replace: (modal: Modal) => void;
	alert: (text: string) => void;
	prompt: (text: string, cont: (text: string | null) => void) => void;
	confirm: (text: string, cont: (confirmed: boolean) => void) => void;
};

type ModalsContextType = [Modal[], ModalsController];

const ModalsContext = createContext<ModalsContextType>();

export const ModalsProvider = (p: ParentProps) => {
	const router = useRouter();
	const [modals, setModals] = createStore<Modal[]>([]);

	// remember what was focused before each modal opened so focus can be
	// restored when it closes (eg. message keyboard navigation)
	const focusStack: (HTMLElement | null)[] = [];
	const rememberFocus = () => {
		const el = document.activeElement;
		focusStack.push(el instanceof HTMLElement ? el : null);
	};
	const restoreFocus = () => {
		const el = focusStack.pop();
		if (!el) return;
		// defer so `inert` is removed from the root before focusing
		queueMicrotask(() => {
			if (document.contains(el)) el.focus();
		});
	};

	const controller: ModalsController = {
		close() {
			const closingModal = modals.at(-1);
			if (!closingModal) return;

			setModals((prev) => prev.slice(0, -1));

			const wasSettings =
				closingModal?.type === "user_settings" ||
				closingModal?.type === "room_settings" ||
				closingModal?.type === "channel_settings";

			if (wasSettings) {
				window.history.pushState(null, "", router.main);
			}

			restoreFocus();
		},
		open(modal: Modal) {
			rememberFocus();
			setModals((prev) => [...prev, modal]);
		},
		replace(modal: Modal) {
			if (modals.at(-1)?.type === modal.type) {
				setModals(modals.length - 1, modal);
			} else {
				setModals((prev) => [...prev.slice(0, -1), modal]);
			}
		},
		alert(text: string) {
			rememberFocus();
			setModals((prev) => [...prev, { type: "alert", text }]);
		},
		prompt(text: string, cont: (text: string | null) => void) {
			const modal: Modal = {
				type: "prompt" as const,
				text,
				cont,
			};
			rememberFocus();
			setModals((prev) => [...prev, modal]);
		},
		confirm(text: string, cont: (confirmed: boolean) => void) {
			const modal: Modal = {
				type: "confirm" as const,
				text,
				cont,
			};
			rememberFocus();
			setModals((prev) => [...prev, modal]);
		},
	};

	// TEMP: for debugging
	(globalThis as typeof globalThis & { modalctl: typeof controller }).modalctl =
		controller;

	return (
		<ModalsContext.Provider value={[modals, controller]}>
			{p.children}
		</ModalsContext.Provider>
	);
};

export const useModals = (): ModalsContextType => {
	const context = useContext(ModalsContext);
	if (!context) {
		throw new Error("useModals must be used within a ModalsProvider");
	}
	return context;
};

export type ModalsContext2Type = {
	open: (modal: Modal) => void;
	alert: (text: string) => void;
	prompt: (text: string) => Promise<string | null>;
	confirm: (text: string) => Promise<boolean>;
	modals: Modal[];
};

export const useModals2 = (): ModalsContext2Type => {
	const context = useContext(ModalsContext);
	if (!context) {
		throw new Error("useModals2 must be used within a ModalsProvider");
	}

	return {
		...context[1],
		get modals() {
			return context[0];
		},
		alert(text: string) {
			context[1].alert(text);
		},
		prompt(text: string): Promise<string | null> {
			return new Promise((resolve) => {
				context[1].prompt(text, resolve);
			});
		},
		confirm(text: string): Promise<boolean> {
			return new Promise((resolve) => {
				context[1].confirm(text, resolve);
			});
		},
	};
};
